import "./HandsDeviationAlert.scss";
import {KeyboardEvent, memo, MouseEvent, SyntheticEvent, useCallback, useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Popover, Tooltip, Whisper} from "rsuite";
import type {WhisperInstance} from "rsuite";
import {HandsDeviationAlertDto} from "../../../api";
import MechanismApi from "../../../services/Mechanism.api";

// События датчиков приходят с блока раз в несколько минут
const REFRESH_MS = 60_000;

const SENSOR_NAMES: Record<string, string> = {
  MainGearSensor: "длительность импульса ведущего триба",
  MinuteGearSensor: "минутная шестерня",
  HourGearSensor: "часовая шестерня",
  IntermediateGearSensor: "промежуточная шестерня",
};

const pad = (value: number): string => String(value).padStart(2, "0");

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const mechanismName = (alert: HandsDeviationAlertDto): string => {
  const parts = [`Механизм ${alert.address ?? ""}`.trim()];
  if (alert.place) parts.push(`«${alert.place.trim()}»`);
  if (alert.serialNumber) parts.push(`№ ${alert.serialNumber}`);
  return parts.join(", ");
};

const deviationText = (alert: HandsDeviationAlertDto): string => {
  const sensor = SENSOR_NAMES[alert.sensor ?? ""] ?? "датчик";
  const values = alert.referenceValue || alert.currentValue
    ? `: ${alert.currentValue ?? "--"} вместо ${alert.referenceValue ?? "--"}`
    : "";
  return `${sensor}${values}`;
};

const periodText = (alert: HandsDeviationAlertDto): string => {
  const since = formatDateTime(alert.since);
  if (alert.resolvedAt) return `${since ? `с ${since} ` : ""}до ${formatDateTime(alert.resolvedAt)}, сейчас в норме`;
  return since ? `с ${since}, продолжается` : "продолжается";
};

// Не даём клику дойти до строки таблицы устройств (она сама открывает станцию)
const stop = (event: SyntheticEvent) => event.stopPropagation();

/**
 * Сбои механизмов по датчикам с обновлением раз в минуту.
 * controlBlockId — одна станция; "all" — все станции пользователя (для списка устройств); null — не загружать
 */
export const useHandsDeviationAlerts = (controlBlockId: string | "all" | null | undefined): HandsDeviationAlertDto[] => {
  const [alerts, setAlerts] = useState<HandsDeviationAlertDto[]>([]);

  const load = useCallback(async () => {
    if (!controlBlockId) {
      setAlerts([]);
      return;
    }
    try {
      setAlerts(controlBlockId === "all"
        ? await MechanismApi.getAllHandsDeviationAlerts()
        : await MechanismApi.getHandsDeviationAlerts(controlBlockId));
    } catch {
      // Значок необязательный: при ошибке просто не показываем
      setAlerts([]);
    }
  }, [controlBlockId]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  return alerts;
};

interface HandsDeviationAlertIconProps {
  alerts: HandsDeviationAlertDto[];
  placement?: "bottom" | "top" | "bottomEnd" | "bottomStart";
  // framed — в рамке, как значки шапки, со списком механизмов; plain — без рамки, как значок ТО в списке устройств, с подсказкой
  variant?: "framed" | "plain";
}

/**
 * Красный «!» (без мигания): есть сбой, который продолжается или прошёл недавно.
 * В выпадающем списке механизмы — ссылки на страницу механизма
 */
export const HandsDeviationAlertIcon = memo(({alerts, placement = "bottom", variant = "framed"}: HandsDeviationAlertIconProps) => {
  const navigate = useNavigate();
  const whisperRef = useRef<WhisperInstance>(null);

  if (alerts.length === 0) return null;

  const active = alerts.some((x) => !x.resolvedAt);

  const open = (alert: HandsDeviationAlertDto) => {
    whisperRef.current?.close();
    navigate(`/devices/${alert.controlBlockId}?tab=devices&externalId=${alert.mechanismId}`);
  };

  const handleClick = (alert: HandsDeviationAlertDto) => (event: MouseEvent) => {
    event.stopPropagation();
    open(alert);
  };

  const handleKey = (alert: HandsDeviationAlertDto) => (event: KeyboardEvent) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    event.stopPropagation();
    open(alert);
  };

  const speaker = (
    <Popover className="hands-deviation-alert__popover" onClick={stop}>
      <div className="hands-deviation-alert__title">
        {active ? "Сбой механизма по датчикам" : "Недавно был сбой механизма по датчикам"}
      </div>
      <ul className="hands-deviation-alert__list">
        {alerts.map((alert) => (
          <li key={`${alert.mechanismId}-${alert.sensor}`}>
            <div
              className="hands-deviation-alert__item"
              role="link"
              tabIndex={0}
              onClick={handleClick(alert)}
              onKeyDown={handleKey(alert)}
            >
              <div className="hands-deviation-alert__mechanism">
                {mechanismName(alert)}
                <span className="hands-deviation-alert__go">Открыть →</span>
              </div>
              <div>{deviationText(alert)}</div>
              <div className="hands-deviation-alert__period">{periodText(alert)}</div>
            </div>
          </li>
        ))}
      </ul>
    </Popover>
  );

  // В списке устройств — короткая подсказка, как у значка ТО; нажатие открывает станцию (строку таблицы)
  if (variant === "plain") {
    return (
      <Whisper placement="top" speaker={<Tooltip>Есть сбои в работе механизмов</Tooltip>}>
        <span className="hands-deviation-alert hands-deviation-alert--plain" aria-label="Есть сбои в работе механизмов">
          !
        </span>
      </Whisper>
    );
  }

  return (
    <Whisper ref={whisperRef} placement={placement} trigger={["hover", "click"]} speaker={speaker} enterable>
      <span
        className={`hands-deviation-alert hands-deviation-alert--${variant}`}
        role="img"
        tabIndex={0}
        aria-label="Сбой механизма по датчикам"
        onClick={stop}
      >
        !
      </span>
    </Whisper>
  );
});

interface HandsDeviationAlertProps {
  controlBlockId: string | null;
}

/**
 * Значок сбоя механизмов для шапки станции
 */
const HandsDeviationAlert = memo(({controlBlockId}: HandsDeviationAlertProps) => {
  const alerts = useHandsDeviationAlerts(controlBlockId);
  return <HandsDeviationAlertIcon alerts={alerts}/>;
});

export default HandsDeviationAlert;
